import { useRouter } from '@tanstack/react-router'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@repo/ui'
import { EllipsisVertical, Plus, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { RagText } from '@/api/content/text'
import { TextDeleteDialog, TextFormDialog } from '@/components/content/ContentDialogs'
import { ContentPagination } from './ContentPagination'
import { PageContent } from './PageContent'
import { PageHeader } from './PageHeader'
import type { BreadcrumbEntry } from './PageHeader'

interface RagTextPageProps {
  breadcrumbs: Array<BreadcrumbEntry>
  items: Array<RagText>
  projectId?: number
  currentPage: number
  pageCount: number
  documentCount?: number | undefined
  searchText: string
  onPageChange: (page: number) => void
  onSearchChange: (text: string) => void
  onClearDocId?: (() => void) | undefined
  embedded?: boolean
  onAuthorityChanged?: () => void | Promise<void>
}

export function RagTextPage({
  breadcrumbs,
  items,
  projectId,
  currentPage,
  pageCount,
  documentCount,
  searchText,
  onPageChange,
  onSearchChange,
  onClearDocId,
  embedded = false,
  onAuthorityChanged
}: RagTextPageProps) {
  const router = useRouter()
  const refreshAuthority = onAuthorityChanged ?? (() => router.invalidate())
  const [editItem, setEditItem] = useState<RagText | null>(null)
  const [deleteItem, setDeleteItem] = useState<RagText | null>(null)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [inputValue, setInputValue] = useState(searchText)
  const [prevSearchText, setPrevSearchText] = useState(searchText)

  if (prevSearchText !== searchText) {
    setPrevSearchText(searchText)
    setInputValue(searchText)
  }

  useEffect(() => {
    const trimmed = inputValue.trim()
    if (trimmed === searchText) return
    const id = setTimeout(() => onSearchChange(trimmed), 300)
    return () => clearTimeout(id)
  }, [inputValue, searchText, onSearchChange])

  const totalItems = documentCount ?? items.length
  const content = (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {totalItems === 0
            ? searchText
              ? 'No matching content.'
              : 'No text content yet.'
            : `${totalItems} item${totalItems === 1 ? '' : 's'}`}
        </p>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
            <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search content..."
              className="w-full pr-9 pl-9"
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => setInputValue('')}
                className="absolute top-1/2 right-2.5 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          <Button size="sm" onClick={() => setShowAddDialog(true)}>
            <Plus className="size-4" />
            Add
          </Button>
        </div>
      </div>

      {onClearDocId && (
        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <span className="text-muted-foreground">Showing a single document.</span>
          <button type="button" onClick={onClearDocId} className="font-medium underline hover:text-foreground">
            View all
          </button>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableBody>
              {items.map((item) => (
                <TextRow
                  key={item.docId}
                  item={item}
                  onEdit={() => setEditItem(item)}
                  onDelete={() => setDeleteItem(item)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pageCount > 1 && (
        <ContentPagination currentPage={currentPage} pageCount={pageCount} onPageChange={onPageChange} />
      )}
    </div>
  )

  return (
    <>
      {!embedded && <PageHeader breadcrumbs={breadcrumbs} />}
      {embedded ? content : <PageContent>{content}</PageContent>}

      <TextFormDialog
        key={showAddDialog ? 'add' : 'add-closed'}
        open={showAddDialog}
        {...(projectId !== undefined ? { projectId } : {})}
        onSaved={refreshAuthority}
        onClose={() => setShowAddDialog(false)}
      />

      <TextFormDialog
        key={editItem ? `edit-${editItem.docId}` : 'edit-closed'}
        open={!!editItem}
        {...(projectId !== undefined ? { projectId } : {})}
        {...(editItem ? { item: editItem } : {})}
        onSaved={refreshAuthority}
        onClose={() => setEditItem(null)}
      />

      <TextDeleteDialog
        item={deleteItem}
        {...(projectId !== undefined ? { projectId } : {})}
        onDeleted={refreshAuthority}
        onClose={() => setDeleteItem(null)}
      />
    </>
  )
}

function formatUpdatedAt(tsMs: number): string {
  return new Date(tsMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function TextRow({ item, onEdit, onDelete }: { item: RagText; onEdit: () => void; onDelete: () => void }) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50 [&_td]:p-4 [&_td]:align-top" onClick={onEdit}>
      <TableCell className="w-full">
        <div
          className="mb-1.5 text-sm leading-relaxed wrap-anywhere"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'pre-line'
          }}
        >
          {item.text}
        </div>
        <div className="text-xs text-muted-foreground">Last updated: {formatUpdatedAt(item.updatedAtTsMs)}</div>
      </TableCell>
      <TableCell className="w-10 align-middle whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {`${item.updatedByFirstName} ${item.updatedBySurname}`.trim()}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" aria-label="Text actions">
                <EllipsisVertical className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  )
}
